// EVRST Studio media picker. Modal grid of curated assets the studio has
// uploaded via Admin → Catalog → Media Library. Click a thumbnail to drop
// it onto the active canvas via the parent's onPick handler.
import { useEffect, useState } from 'react'
import { useAuth } from '@workos-inc/authkit-react'
import { apiFetch, useApiSrc } from '../lib/api'

export interface StudioAsset {
  id: string
  originalName: string
  url: string
  mimeType: string
  widthPx: number | null
  heightPx: number | null
  createdAt: string
}

interface Props {
  open: boolean
  onClose: () => void
  onPick: (asset: StudioAsset) => void
}

export default function StudioMediaPicker({ open, onClose, onPick }: Props) {
  const { getAccessToken } = useAuth()
  const apiSrc = useApiSrc()
  const [items, setItems] = useState<StudioAsset[]>([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    let mounted = true
    setLoading(true)
    const qs = search ? `?search=${encodeURIComponent(search)}` : ''
    apiFetch<{ items: StudioAsset[] }>(getAccessToken as unknown as () => Promise<string | undefined>, `/api/studio-media${qs}`)
      .then((d) => { if (mounted) { setItems(d.items); setErr(null) } })
      .catch((e) => { if (mounted) setErr((e as Error).message) })
      .finally(() => { if (mounted) setLoading(false) })
    return () => { mounted = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, search])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-border flex items-center justify-between gap-4">
          <div>
            <div className="eyebrow !mb-0">EVRST Studio</div>
            <h3 className="font-heading uppercase font-bold text-xl text-ink">Media Library</h3>
            <p className="text-xs text-ink-3 mt-1">Curated artwork from the studio. Click any item to drop it onto your design.</p>
          </div>
          <button onClick={onClose} className="text-ink-3 hover:text-ink text-2xl leading-none shrink-0">×</button>
        </div>

        <div className="px-6 py-3 border-b border-border">
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search the library…" className="input max-w-sm" />
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {err && <div className="card text-red-700 mb-4">{err}</div>}
          {loading && items.length === 0 && <div className="text-sm text-ink-3 text-center py-10">Loading…</div>}
          {!loading && items.length === 0 && (
            <div className="text-center py-12 text-ink-3">
              <div className="text-sm">No studio media available yet.</div>
              <div className="text-[11px] mt-1">An admin can upload curated artwork via Admin → Catalog → Media Library.</div>
            </div>
          )}
          {items.length > 0 && (
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {items.map((a) => (
                <button key={a.id} onClick={() => { onPick(a); onClose() }} title={a.originalName}
                  className="aspect-square bg-zinc-900 border border-zinc-700 rounded overflow-hidden hover:border-gold transition-colors flex items-center justify-center group relative">
                  <img src={apiSrc(a.url)} alt={a.originalName} className="max-w-full max-h-full object-contain p-2" />
                  <span className="absolute bottom-0 left-0 right-0 text-[10px] text-white bg-black/60 px-2 py-1 truncate opacity-0 group-hover:opacity-100 transition-opacity">{a.originalName}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
